
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Crown, Users, Clock, Plus, X, Edit, Save, AlertCircle, CheckCircle2, Calendar } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import UserSelector from './UserSelector';

interface UserTier {
  ID: number;
  user_id: number;
  tier_level: number;
  tier_name: string;
  trial_start_date?: string;
  trial_expires_at?: string;
  trial_used?: boolean;
  assigned_by: number;
  assigned_at: string;
  user_email?: string;
  user_name?: string;
}

interface User {
  ID: number;
  Name: string;
  Email: string;
  CreateTime: string;
}

const UserTierManager: React.FC = () => {
  const { user, isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const [userTiers, setUserTiers] = useState<UserTier[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newTierLevel, setNewTierLevel] = useState<number>(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<UserTier | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const tierConfigs = {
    1: {
      name: 'Basic',
      color: 'blue',
      description: 'Basic AI access with 7-day trial',
      features: ['7-day premium trial', 'Basic AI tools', 'Limited exports']
    },
    2: {
      name: 'Premium',
      color: 'purple',
      description: 'Full AI access',
      features: ['Unlimited AI tools', 'Advanced features', 'Priority support', 'Unlimited exports']
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      loadUserTiers();
      loadAllUsers();
    }
  }, [isSuperAdmin]);

  const loadUserTiers = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await window.ezsite.apis.tablePage(15216, {
        PageNo: 1,
        PageSize: 1000,
        OrderByField: 'ID',
        IsAsc: false,
        Filters: []
      });

      if (error) throw error;

      if (data?.List) {
        // Fetch user details for each tier
        const tiersWithUserInfo = await Promise.all(
          data.List.map(async (tier: UserTier) => {
            try {
              const userResponse = await window.ezsite.apis.getUserInfo();
              if (!userResponse.error) {
                return {
                  ...tier,
                  user_email: userResponse.data?.Email || 'Unknown',
                  user_name: userResponse.data?.Name || 'Unknown'
                };
              }
            } catch (error) {
              console.error('Error fetching user info for tier:', tier.ID, error);
            }
            return tier;
          })
        );

        setUserTiers(tiersWithUserInfo);
      }
    } catch (error) {
      console.error('Error loading user tiers:', error);
      toast({
        title: 'Error',
        description: 'Failed to load user tiers',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      // This is a placeholder since we can't directly query the User table
      // In a real implementation, you would have an API endpoint to get all users
      setAllUsers([]);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const assignTier = async () => {
    if (!selectedUser) return;

    setIsUpdating(true);
    try {
      // Check if user already has a tier
      const existingTier = userTiers.find((tier) => tier.user_id === selectedUser.ID);

      const tierData = {
        user_id: selectedUser.ID,
        tier_level: newTierLevel,
        tier_name: tierConfigs[newTierLevel as keyof typeof tierConfigs].name,
        assigned_by: user?.ID || 0,
        assigned_at: new Date().toISOString(),
        trial_used: false
      };

      let result;
      if (existingTier) {
        // Update existing tier
        result = await window.ezsite.apis.tableUpdate(15216, {
          ID: existingTier.ID,
          ...tierData
        });
      } else {
        // Create new tier
        result = await window.ezsite.apis.tableCreate(15216, tierData);
      }

      if (result.error) throw result.error;

      toast({
        title: 'Success',
        description: `User tier ${existingTier ? 'updated' : 'assigned'} successfully`
      });

      setIsDialogOpen(false);
      setSelectedUser(null);
      loadUserTiers();
    } catch (error) {
      console.error('Error assigning tier:', error);
      toast({
        title: 'Error',
        description: 'Failed to assign tier',
        variant: 'destructive'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const updateTier = async (tier: UserTier, updates: Partial<UserTier>) => {
    setIsUpdating(true);
    try {
      const { error } = await window.ezsite.apis.tableUpdate(15216, {
        ID: tier.ID,
        ...updates
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Tier updated successfully'
      });

      loadUserTiers();
    } catch (error) {
      console.error('Error updating tier:', error);
      toast({
        title: 'Error',
        description: 'Failed to update tier',
        variant: 'destructive'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const resetTrial = async (tier: UserTier) => {
    const now = new Date();
    const expiryDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    await updateTier(tier, {
      trial_start_date: now.toISOString(),
      trial_expires_at: expiryDate.toISOString(),
      trial_used: true
    });
  };

  const extendTrial = async (tier: UserTier, days: number) => {
    const currentExpiry = tier.trial_expires_at ? new Date(tier.trial_expires_at) : new Date();
    const newExpiry = new Date(currentExpiry.getTime() + days * 24 * 60 * 60 * 1000);

    await updateTier(tier, {
      trial_expires_at: newExpiry.toISOString()
    });
  };

  const calculateTrialStatus = (tier: UserTier) => {
    if (!tier.trial_start_date || !tier.trial_expires_at) {
      return { status: 'not_started', daysRemaining: 0, label: 'Not Started' };
    }

    const now = new Date();
    const expiry = new Date(tier.trial_expires_at);
    const isActive = now < expiry;
    const timeDiff = expiry.getTime() - now.getTime();
    const daysLeft = Math.max(0, Math.ceil(timeDiff / (1000 * 3600 * 24)));

    if (!isActive) {
      return { status: 'expired', daysRemaining: 0, label: 'Expired' };
    }

    if (daysLeft <= 1) {
      return { status: 'expiring', daysRemaining: daysLeft, label: `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left` };
    }

    return { status: 'active', daysRemaining: daysLeft, label: `${daysLeft} days left` };
  };

  const filteredTiers = userTiers.filter((tier) =>
  tier.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  tier.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  tier.tier_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tierStats = {
    total: userTiers.length,
    basic: userTiers.filter((t) => t.tier_level === 1).length,
    premium: userTiers.filter((t) => t.tier_level === 2).length,
    activeTrial: userTiers.filter((t) => {
      if (t.tier_level !== 1 || !t.trial_expires_at) return false;
      return new Date() < new Date(t.trial_expires_at);
    }).length
  };

  if (!isSuperAdmin) {
    return (
      <Alert data-id="8rz8j9wki" data-path="src/components/superadmin/UserTierManager.tsx">
        <AlertCircle className="h-4 w-4" data-id="go9gjkxss" data-path="src/components/superadmin/UserTierManager.tsx" />
        <AlertDescription data-id="55cxmnz43" data-path="src/components/superadmin/UserTierManager.tsx">
          You need Super Admin privileges to access the User Tier Manager.
        </AlertDescription>
      </Alert>);

  }

  return (
    <div className="space-y-6" data-id="hf17l2v4c" data-path="src/components/superadmin/UserTierManager.tsx">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="ofziagj4j" data-path="src/components/superadmin/UserTierManager.tsx">
        <div data-id="edsit511q" data-path="src/components/superadmin/UserTierManager.tsx">
          <h2 className="text-2xl font-bold text-gray-900" data-id="jldeporh5" data-path="src/components/superadmin/UserTierManager.tsx">User Tier Manager</h2>
          <p className="text-gray-600" data-id="93crft2tl" data-path="src/components/superadmin/UserTierManager.tsx">Manage user access levels and trial periods</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} data-id="pb8yw4oy8" data-path="src/components/superadmin/UserTierManager.tsx">
          <DialogTrigger asChild data-id="bqqtnwsh4" data-path="src/components/superadmin/UserTierManager.tsx">
            <Button className="bg-gradient-to-r from-purple-600 to-blue-600" data-id="wv3rsa0b2" data-path="src/components/superadmin/UserTierManager.tsx">
              <Plus className="h-4 w-4 mr-2" data-id="uf86lnuqp" data-path="src/components/superadmin/UserTierManager.tsx" />
              Assign Tier
            </Button>
          </DialogTrigger>
          <DialogContent data-id="9n8pv5pcm" data-path="src/components/superadmin/UserTierManager.tsx">
            <DialogHeader data-id="met04t3oy" data-path="src/components/superadmin/UserTierManager.tsx">
              <DialogTitle data-id="a5em8myls" data-path="src/components/superadmin/UserTierManager.tsx">Assign User Tier</DialogTitle>
              <DialogDescription data-id="snvzk9vw2" data-path="src/components/superadmin/UserTierManager.tsx">
                Select a user and assign them a tier level
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4" data-id="xdux4ec7p" data-path="src/components/superadmin/UserTierManager.tsx">
              <UserSelector
                onUserSelect={setSelectedUser}
                selectedUser={selectedUser} data-id="rp8rdq2q6" data-path="src/components/superadmin/UserTierManager.tsx" />

              
              <div data-id="bz7f7c1w5" data-path="src/components/superadmin/UserTierManager.tsx">
                <Label data-id="qk9ku48cu" data-path="src/components/superadmin/UserTierManager.tsx">Tier Level</Label>
                <Select value={newTierLevel.toString()} onValueChange={(value) => setNewTierLevel(parseInt(value))} data-id="n4c2ibbwl" data-path="src/components/superadmin/UserTierManager.tsx">
                  <SelectTrigger data-id="jx0ce15db" data-path="src/components/superadmin/UserTierManager.tsx">
                    <SelectValue data-id="cil5q3mrd" data-path="src/components/superadmin/UserTierManager.tsx" />
                  </SelectTrigger>
                  <SelectContent data-id="sswcvt3aj" data-path="src/components/superadmin/UserTierManager.tsx">
                    {Object.entries(tierConfigs).map(([level, config]) =>
                    <SelectItem key={level} value={level} data-id="0av6loecw" data-path="src/components/superadmin/UserTierManager.tsx">
                        <div className="flex items-center space-x-2" data-id="qy1rfpygd" data-path="src/components/superadmin/UserTierManager.tsx">
                          <Crown className="h-4 w-4" data-id="96jer7wh4" data-path="src/components/superadmin/UserTierManager.tsx" />
                          <span data-id="9sjvhcfr7" data-path="src/components/superadmin/UserTierManager.tsx">{config.name} (Level {level})</span>
                        </div>
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={assignTier}
                disabled={!selectedUser || isUpdating}
                className="w-full" data-id="u0tg856bl" data-path="src/components/superadmin/UserTierManager.tsx">

                {isUpdating ? 'Assigning...' : 'Assign Tier'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="0i0xgpa2h" data-path="src/components/superadmin/UserTierManager.tsx">
        <Card data-id="kje31jt1a" data-path="src/components/superadmin/UserTierManager.tsx">
          <CardContent className="p-4" data-id="dtkniag8l" data-path="src/components/superadmin/UserTierManager.tsx">
            <div className="flex items-center space-x-2" data-id="790h300dc" data-path="src/components/superadmin/UserTierManager.tsx">
              <Users className="h-5 w-5 text-gray-500" data-id="yj2ar040d" data-path="src/components/superadmin/UserTierManager.tsx" />
              <div data-id="psm22gc53" data-path="src/components/superadmin/UserTierManager.tsx">
                <p className="text-sm text-gray-600" data-id="4robtjpme" data-path="src/components/superadmin/UserTierManager.tsx">Total Users</p>
                <p className="text-2xl font-bold" data-id="w5kvygcwi" data-path="src/components/superadmin/UserTierManager.tsx">{tierStats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card data-id="br2vjn9k2" data-path="src/components/superadmin/UserTierManager.tsx">
          <CardContent className="p-4" data-id="omjfarfea" data-path="src/components/superadmin/UserTierManager.tsx">
            <div className="flex items-center space-x-2" data-id="kzt9h4li1" data-path="src/components/superadmin/UserTierManager.tsx">
              <Badge variant="outline" className="text-blue-600 border-blue-600" data-id="95l29d823" data-path="src/components/superadmin/UserTierManager.tsx">
                Basic
              </Badge>
              <div data-id="zq50mawoa" data-path="src/components/superadmin/UserTierManager.tsx">
                <p className="text-sm text-gray-600" data-id="q9r6fw2g2" data-path="src/components/superadmin/UserTierManager.tsx">Basic Users</p>
                <p className="text-2xl font-bold" data-id="jmebbn6o7" data-path="src/components/superadmin/UserTierManager.tsx">{tierStats.basic}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="6mutrltqu" data-path="src/components/superadmin/UserTierManager.tsx">
          <CardContent className="p-4" data-id="5xq1ptfnj" data-path="src/components/superadmin/UserTierManager.tsx">
            <div className="flex items-center space-x-2" data-id="2bu5kbuwr" data-path="src/components/superadmin/UserTierManager.tsx">
              <Badge variant="outline" className="text-purple-600 border-purple-600" data-id="kimisxvvs" data-path="src/components/superadmin/UserTierManager.tsx">
                Premium
              </Badge>
              <div data-id="hod7e5b3u" data-path="src/components/superadmin/UserTierManager.tsx">
                <p className="text-sm text-gray-600" data-id="tp6jpb4ny" data-path="src/components/superadmin/UserTierManager.tsx">Premium Users</p>
                <p className="text-2xl font-bold" data-id="9kcyqsi1i" data-path="src/components/superadmin/UserTierManager.tsx">{tierStats.premium}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="cnzs3kylu" data-path="src/components/superadmin/UserTierManager.tsx">
          <CardContent className="p-4" data-id="sl64j7zs9" data-path="src/components/superadmin/UserTierManager.tsx">
            <div className="flex items-center space-x-2" data-id="7wjso0gw6" data-path="src/components/superadmin/UserTierManager.tsx">
              <Clock className="h-5 w-5 text-green-500" data-id="kv8arx4iy" data-path="src/components/superadmin/UserTierManager.tsx" />
              <div data-id="44hqleaao" data-path="src/components/superadmin/UserTierManager.tsx">
                <p className="text-sm text-gray-600" data-id="pyexp6mqm" data-path="src/components/superadmin/UserTierManager.tsx">Active Trials</p>
                <p className="text-2xl font-bold" data-id="e7vcytihh" data-path="src/components/superadmin/UserTierManager.tsx">{tierStats.activeTrial}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center space-x-4" data-id="ourxlroho" data-path="src/components/superadmin/UserTierManager.tsx">
        <Input
          placeholder="Search by email, name, or tier..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md" data-id="1npqnsim1" data-path="src/components/superadmin/UserTierManager.tsx" />

      </div>

      {/* User Tiers Table */}
      <Card data-id="ew0agnpll" data-path="src/components/superadmin/UserTierManager.tsx">
        <CardHeader data-id="9y86gjsl2" data-path="src/components/superadmin/UserTierManager.tsx">
          <CardTitle data-id="a48xpp6pb" data-path="src/components/superadmin/UserTierManager.tsx">User Tiers</CardTitle>
          <CardDescription data-id="m145n4pqg" data-path="src/components/superadmin/UserTierManager.tsx">
            Manage user access levels, trial periods, and permissions
          </CardDescription>
        </CardHeader>
        <CardContent data-id="yc8d9tzb9" data-path="src/components/superadmin/UserTierManager.tsx">
          {isLoading ?
          <div className="text-center py-8" data-id="62t0351zf" data-path="src/components/superadmin/UserTierManager.tsx">Loading...</div> :

          <div className="overflow-x-auto" data-id="ykmoy0pm4" data-path="src/components/superadmin/UserTierManager.tsx">
              <Table data-id="0tbrfsukt" data-path="src/components/superadmin/UserTierManager.tsx">
                <TableHeader data-id="34kc4aeu6" data-path="src/components/superadmin/UserTierManager.tsx">
                  <TableRow data-id="ysgxr0b4j" data-path="src/components/superadmin/UserTierManager.tsx">
                    <TableHead data-id="zzhphbqvb" data-path="src/components/superadmin/UserTierManager.tsx">User</TableHead>
                    <TableHead data-id="4k9z5s244" data-path="src/components/superadmin/UserTierManager.tsx">Tier</TableHead>
                    <TableHead data-id="2y992sthc" data-path="src/components/superadmin/UserTierManager.tsx">Trial Status</TableHead>
                    <TableHead data-id="qz9k8h1uu" data-path="src/components/superadmin/UserTierManager.tsx">Trial Period</TableHead>
                    <TableHead data-id="8dwtmj52m" data-path="src/components/superadmin/UserTierManager.tsx">Assigned</TableHead>
                    <TableHead data-id="ri5s02h7k" data-path="src/components/superadmin/UserTierManager.tsx">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-id="jvyh86r49" data-path="src/components/superadmin/UserTierManager.tsx">
                  {filteredTiers.map((tier) => {
                  const trialStatus = calculateTrialStatus(tier);
                  const config = tierConfigs[tier.tier_level as keyof typeof tierConfigs];

                  return (
                    <TableRow key={tier.ID} data-id="wukvca4tu" data-path="src/components/superadmin/UserTierManager.tsx">
                        <TableCell data-id="mjvyb9dgf" data-path="src/components/superadmin/UserTierManager.tsx">
                          <div data-id="yvcwqpju8" data-path="src/components/superadmin/UserTierManager.tsx">
                            <p className="font-medium" data-id="bhyef5mhw" data-path="src/components/superadmin/UserTierManager.tsx">{tier.user_name || 'Unknown'}</p>
                            <p className="text-sm text-gray-500" data-id="stfanglrf" data-path="src/components/superadmin/UserTierManager.tsx">{tier.user_email || 'No email'}</p>
                          </div>
                        </TableCell>
                        
                        <TableCell data-id="xaes8mkuv" data-path="src/components/superadmin/UserTierManager.tsx">
                          <Badge
                          variant="outline"
                          className={`${config.color === 'blue' ? 'text-blue-600 border-blue-600' : 'text-purple-600 border-purple-600'}`} data-id="z4ex77ybu" data-path="src/components/superadmin/UserTierManager.tsx">

                            <Crown className="h-3 w-3 mr-1" data-id="70l2u8p59" data-path="src/components/superadmin/UserTierManager.tsx" />
                            {config.name}
                          </Badge>
                        </TableCell>

                        <TableCell data-id="eq7nr3ard" data-path="src/components/superadmin/UserTierManager.tsx">
                          {tier.tier_level === 1 ?
                        <Badge
                          variant="outline"
                          className={
                          trialStatus.status === 'active' ? 'text-green-600 border-green-600' :
                          trialStatus.status === 'expiring' ? 'text-orange-600 border-orange-600' :
                          trialStatus.status === 'expired' ? 'text-red-600 border-red-600' :
                          'text-gray-600 border-gray-600'
                          } data-id="ctvzr9z4m" data-path="src/components/superadmin/UserTierManager.tsx">

                              {trialStatus.status === 'active' && <CheckCircle2 className="h-3 w-3 mr-1" data-id="0epd4ry2f" data-path="src/components/superadmin/UserTierManager.tsx" />}
                              {trialStatus.status === 'expiring' && <Clock className="h-3 w-3 mr-1" data-id="lfw08rizc" data-path="src/components/superadmin/UserTierManager.tsx" />}
                              {trialStatus.status === 'expired' && <X className="h-3 w-3 mr-1" data-id="umzh48hqk" data-path="src/components/superadmin/UserTierManager.tsx" />}
                              {trialStatus.status === 'not_started' && <Calendar className="h-3 w-3 mr-1" data-id="q06d13zo9" data-path="src/components/superadmin/UserTierManager.tsx" />}
                              {trialStatus.label}
                            </Badge> :

                        <span className="text-gray-400" data-id="5x9d6qhqa" data-path="src/components/superadmin/UserTierManager.tsx">N/A</span>
                        }
                        </TableCell>

                        <TableCell data-id="uljotkef8" data-path="src/components/superadmin/UserTierManager.tsx">
                          {tier.tier_level === 1 && tier.trial_start_date ?
                        <div className="text-sm" data-id="zxtqfjdi5" data-path="src/components/superadmin/UserTierManager.tsx">
                              <p data-id="5cw9d0x0e" data-path="src/components/superadmin/UserTierManager.tsx">Started: {new Date(tier.trial_start_date).toLocaleDateString()}</p>
                              {tier.trial_expires_at &&
                          <p data-id="jpawob14x" data-path="src/components/superadmin/UserTierManager.tsx">Expires: {new Date(tier.trial_expires_at).toLocaleDateString()}</p>
                          }
                            </div> :

                        <span className="text-gray-400" data-id="zqcfcpg2o" data-path="src/components/superadmin/UserTierManager.tsx">No trial</span>
                        }
                        </TableCell>

                        <TableCell data-id="hdpd0mom8" data-path="src/components/superadmin/UserTierManager.tsx">
                          <div className="text-sm text-gray-500" data-id="zg6gq0wgx" data-path="src/components/superadmin/UserTierManager.tsx">
                            {new Date(tier.assigned_at).toLocaleDateString()}
                          </div>
                        </TableCell>

                        <TableCell data-id="feh4vjh62" data-path="src/components/superadmin/UserTierManager.tsx">
                          <div className="flex space-x-2" data-id="0t9jhrh7w" data-path="src/components/superadmin/UserTierManager.tsx">
                            <Select
                            value={tier.tier_level.toString()}
                            onValueChange={(value) => updateTier(tier, {
                              tier_level: parseInt(value),
                              tier_name: tierConfigs[parseInt(value) as keyof typeof tierConfigs].name
                            })} data-id="bh86vdmiy" data-path="src/components/superadmin/UserTierManager.tsx">

                              <SelectTrigger className="w-24" data-id="jkqfpd8zt" data-path="src/components/superadmin/UserTierManager.tsx">
                                <SelectValue data-id="ywar70cg0" data-path="src/components/superadmin/UserTierManager.tsx" />
                              </SelectTrigger>
                              <SelectContent data-id="g3e8iptax" data-path="src/components/superadmin/UserTierManager.tsx">
                                {Object.entries(tierConfigs).map(([level, config]) =>
                              <SelectItem key={level} value={level} data-id="oetgfacnc" data-path="src/components/superadmin/UserTierManager.tsx">
                                    {config.name}
                                  </SelectItem>
                              )}
                              </SelectContent>
                            </Select>

                            {tier.tier_level === 1 &&
                          <div className="flex space-x-1" data-id="ukkhc5yx0" data-path="src/components/superadmin/UserTierManager.tsx">
                                <Button
                              size="sm"
                              variant="outline"
                              onClick={() => resetTrial(tier)}
                              disabled={isUpdating} data-id="xyq3zvjhh" data-path="src/components/superadmin/UserTierManager.tsx">

                                  Reset Trial
                                </Button>
                                <Button
                              size="sm"
                              variant="outline"
                              onClick={() => extendTrial(tier, 7)}
                              disabled={isUpdating} data-id="zx0lenxff" data-path="src/components/superadmin/UserTierManager.tsx">

                                  +7 Days
                                </Button>
                              </div>
                          }
                          </div>
                        </TableCell>
                      </TableRow>);

                })}
                </TableBody>
              </Table>
            </div>
          }
        </CardContent>
      </Card>
    </div>);

};

export default UserTierManager;